import { useRef } from "react";
import { screenToCanvas } from "../../ScreenCordToCanvasCord";
import { CanvasDrawer } from "../../CanvasDrawer";
import { CanvasImage } from "../../CanvasImage";
import { useStickerSectionStore } from "../../EditorStore";
import { UndoRedoHelper } from "../../UndoRedoHelper";
import { ImageStore } from "../../ImageStore";

export default function useSelector() {

    const setCanvasImageSelected = useStickerSectionStore((state) => state.setCanvasImageSelected)
    const currentSelectedCanvasImageRef = useStickerSectionStore((state) => state.currentCanvasImageSelectedRef)
    const setLayerStatusInStore = useStickerSectionStore((state) => state.setLayerStatus);
    const setUndoRedo = useStickerSectionStore((state) => state.setUndoRedo)

    const lastTouchPosition = useRef<null | { x: number; y: number }>(null);
    const selectedImageActionRef = useRef<null | string>(null);
    const initialRotationRef = useRef<number | null>(null);
    const initialAngleRef = useRef<number | null>(null);
    const initialBoundsRef = useRef<{
        TL: { x: number; y: number };
        BR: { x: number; y: number };
    } | null>(null);
    const aspectRatioRef = useRef<number | null>(null);
    const initialPointerLocalPosRef = useRef<{ x: number; y: number } | null>(null);

    const actionRef = useRef(false)

    const updateUndoRedo = () => {

        const dict = { canUndo: false, canRedo: false }

        if (UndoRedoHelper.undoStack.length > 0) {
            dict.canUndo = true
        }

        setUndoRedo(dict)
    }

    const updateLayerStatus = (image: CanvasImage | null) => {
        let newAtBottom = false;
        let newAtTop = false;

        if (image && CanvasDrawer.canvasImages && CanvasDrawer.canvasImages.length > 0) {
            if (image.itemIndex === 0 && CanvasDrawer.canvasImages.length === 1) {
                newAtBottom = true;
                newAtTop = true;
            } else if (image.itemIndex === 0) {
                newAtBottom = true;
                newAtTop = false;
            } else if (image.itemIndex === CanvasDrawer.canvasImages.length - 1) {
                newAtBottom = false;
                newAtTop = true;
            } else {
                newAtBottom = false;
                newAtTop = false;
            }
        }
        setLayerStatusInStore({ isAtTop: newAtTop, isAtBottom: newAtBottom });
    };

    let lastRender = 0;

    const startSelect = (e: React.TouchEvent<HTMLCanvasElement>) => {
        if (!CanvasDrawer.canvas) return;
        const cords = screenToCanvas(e, CanvasDrawer.canvas);

        let selectedImageAction = false;
        let foundCanvasImage = false;

        if (currentSelectedCanvasImageRef.current) {
            const action = currentSelectedCanvasImageRef.current.getSelectorActionAt(
                cords.x,
                cords.y
            );
            if (action) {
                selectedImageAction = true;
                selectedImageActionRef.current = action;

                if (action === "rotate") {
                    const image = currentSelectedCanvasImageRef.current;
                    const TL = image.TL_imageBounds;
                    const BR = image.BR_imageBounds;
                    const centerX = TL.x + (BR.x - TL.x) / 2;
                    const centerY = TL.y + (BR.y - TL.y) / 2;

                    const dx = cords.x - centerX;
                    const dy = cords.y - centerY;
                    const initialAngle = (Math.atan2(dy, dx) * 180) / Math.PI;

                    initialRotationRef.current = image.rotation;
                    initialAngleRef.current = initialAngle;
                } else if (action.startsWith("resize-")) {
                    const image = currentSelectedCanvasImageRef.current;
                    initialBoundsRef.current = {
                        TL: { ...image.TL_imageBounds },
                        BR: { ...image.BR_imageBounds },
                    };
                    const width =
                        initialBoundsRef.current.BR.x - initialBoundsRef.current.TL.x;
                    const height =
                        initialBoundsRef.current.BR.y - initialBoundsRef.current.TL.y;
                    aspectRatioRef.current = width / height;

                    const centerX =
                        (initialBoundsRef.current.TL.x + initialBoundsRef.current.BR.x) / 2;
                    const centerY =
                        (initialBoundsRef.current.TL.y + initialBoundsRef.current.BR.y) / 2;

                    const angleRad = (-image.rotation * Math.PI) / 180;
                    const dx = cords.x - centerX;
                    const dy = cords.y - centerY;

                    const localX = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
                    const localY = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);

                    initialPointerLocalPosRef.current = { x: localX, y: localY };
                }
            } else {
                selectedImageActionRef.current = null;
            }
        }

        if (!selectedImageAction) {
            for (let i = CanvasDrawer.canvasImages.length - 1; i >= 0; i--) {
                const image = CanvasDrawer.canvasImages[i];
                const TL = image.TL_imageBounds;
                const BR = image.BR_imageBounds;

                const width = BR.x - TL.x;
                const height = BR.y - TL.y;

                const centerX = TL.x + width / 2;
                const centerY = TL.y + height / 2;

                const dx = cords.x - centerX;
                const dy = cords.y - centerY;

                const angleRad = (-image.rotation * Math.PI) / 180;
                const localX = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
                const localY = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);

                const withinX = localX >= -width / 2 && localX <= width / 2;
                const withinY = localY >= -height / 2 && localY <= height / 2;

                if (withinX && withinY) {
                    const namedImage = ImageStore.namedImages.find(ni => ni.imgID === image.imageID);
                    if (namedImage) {
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = namedImage.img.width;
                        tempCanvas.height = namedImage.img.height;
                        const tempCtx = tempCanvas.getContext('2d');

                        if (tempCtx) {
                            tempCtx.drawImage(namedImage.img, 0, 0);

                            const imgX = (localX + width / 2) * (namedImage.img.width / width);
                            const imgY = (localY + height / 2) * (namedImage.img.height / height);

                            const pixelData = tempCtx.getImageData(Math.floor(imgX), Math.floor(imgY), 1, 1).data;
                            const alpha = pixelData[3];

                            if (alpha < 10) {
                                continue;
                            }
                        }
                    }

                    if (currentSelectedCanvasImageRef.current)
                        currentSelectedCanvasImageRef.current.selected = false;

                    currentSelectedCanvasImageRef.current = image;

                    updateLayerStatus(image)

                    updateUndoRedo()

                    setCanvasImageSelected(true)
                    image.selected = true;
                    foundCanvasImage = true;
                    CanvasDrawer.rerenderCanvas();
                    lastTouchPosition.current = { x: cords.x, y: cords.y };
                    break;
                }
            }
        }

        if (!foundCanvasImage && currentSelectedCanvasImageRef.current && !selectedImageAction) {
            currentSelectedCanvasImageRef.current.selected = false;
            currentSelectedCanvasImageRef.current = null;
            currentSelectedCanvasImageRef.current = null
            setCanvasImageSelected(false)
            CanvasDrawer.rerenderCanvas();
        }

        lastTouchPosition.current = { x: cords.x, y: cords.y };
    };

    const selectMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
        const MIN_STICKER_AREA = 75000

        if (!CanvasDrawer.canvas) return;
        const cords = screenToCanvas(e, CanvasDrawer.canvas);

        const image = currentSelectedCanvasImageRef.current;
        const action = selectedImageActionRef.current;

        if (!image || !lastTouchPosition.current) return;

        if (!action) {
            const diffX = cords.x - lastTouchPosition.current.x;
            const diffY = cords.y - lastTouchPosition.current.y;

            image.BR_imageBounds.x += diffX;
            image.TL_imageBounds.x += diffX;
            image.BR_imageBounds.y += diffY;
            image.TL_imageBounds.y += diffY;

            const now = performance.now();
            if (now - lastRender > 10) {
                CanvasDrawer.rerenderCanvas();
                lastRender = now;
            }

            actionRef.current = true;
        } else if (action === "rotate") {
            if (initialRotationRef.current === null || initialAngleRef.current === null) return;

            const TL = image.TL_imageBounds;
            const BR = image.BR_imageBounds;
            const centerX = TL.x + (BR.x - TL.x) / 2;
            const centerY = TL.y + (BR.y - TL.y) / 2;

            const dx = cords.x - centerX;
            const dy = cords.y - centerY;
            const currentAngle = (Math.atan2(dy, dx) * 180) / Math.PI;

            const angleDiff = currentAngle - initialAngleRef.current;
            image.rotation = initialRotationRef.current + angleDiff;

            CanvasDrawer.rerenderCanvas();

            actionRef.current = true;
        } else if (action.startsWith("resize-")) {
            if (!initialBoundsRef.current || aspectRatioRef.current === null || !initialPointerLocalPosRef.current)
                return;

            const initialTL = initialBoundsRef.current.TL;
            const initialBR = initialBoundsRef.current.BR;
            const aspectRatio = aspectRatioRef.current;

            const centerX = (initialTL.x + initialBR.x) / 2;
            const centerY = (initialTL.y + initialBR.y) / 2;

            const angleRad = (-image.rotation * Math.PI) / 180;
            const dx = cords.x - centerX;
            const dy = cords.y - centerY;

            const localX = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
            const localY = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);

            const initialLocalX = initialPointerLocalPosRef.current.x;
            const initialLocalY = initialPointerLocalPosRef.current.y;

            let newWidth = 0;
            let newHeight = 0;

            actionRef.current = true;

            switch (action) {
                case "resize-tl": {
                    let widthChange = initialLocalX - localX;
                    let heightChange = initialLocalY - localY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newTLx = centerX - newWidth / 2;
                    const newTLy = centerY - newHeight / 2;

                    image.TL_imageBounds.x = newTLx;
                    image.TL_imageBounds.y = newTLy;
                    image.BR_imageBounds.x = centerX + newWidth / 2;
                    image.BR_imageBounds.y = centerY + newHeight / 2;
                    break;
                }
                case "resize-tr": {
                    let widthChange = localX - initialLocalX;
                    let heightChange = initialLocalY - localY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newBRx = centerX + newWidth / 2;
                    const newTLy = centerY - newHeight / 2;

                    image.BR_imageBounds.x = newBRx;
                    image.TL_imageBounds.y = newTLy;
                    image.TL_imageBounds.x = newBRx - newWidth;
                    image.BR_imageBounds.y = newTLy + newHeight;
                    break;
                }
                case "resize-bl": {
                    let widthChange = initialLocalX - localX;
                    let heightChange = localY - initialLocalY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newTLx = centerX - newWidth / 2;
                    const newBRy = centerY + newHeight / 2;

                    image.TL_imageBounds.x = newTLx;
                    image.BR_imageBounds.y = newBRy;
                    image.BR_imageBounds.x = newTLx + newWidth;
                    image.TL_imageBounds.y = newBRy - newHeight;
                    break;
                }
                case "resize-br": {
                    let widthChange = localX - initialLocalX;
                    let heightChange = localY - initialLocalY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newBRx = centerX + newWidth / 2;
                    const newBRy = centerY + newHeight / 2;

                    image.BR_imageBounds.x = newBRx;
                    image.BR_imageBounds.y = newBRy;
                    image.TL_imageBounds.x = newBRx - newWidth;
                    image.TL_imageBounds.y = newBRy - newHeight;
                    break;
                }
            }

            if (image.BR_imageBounds.x < image.TL_imageBounds.x + 10) {
                image.BR_imageBounds.x = image.TL_imageBounds.x + 10;
            }
            if (image.BR_imageBounds.y < image.TL_imageBounds.y + 10) {
                image.BR_imageBounds.y = image.TL_imageBounds.y + 10;
            }

            CanvasDrawer.rerenderCanvas();
        }

        lastTouchPosition.current = { x: cords.x, y: cords.y };
    };


    const endSelect = () => {
        if (actionRef.current == true) {
            UndoRedoHelper.createSnapShot()
            updateUndoRedo()
        }

        actionRef.current = false
        lastTouchPosition.current = null
        selectedImageActionRef.current = null
    }

    const startSelectMouse = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!CanvasDrawer.canvas) return;
        const cords = screenToCanvas(e, CanvasDrawer.canvas)

        let selectedImageAction = false;
        let foundCanvasImage = false;

        if (currentSelectedCanvasImageRef.current) {
            const action = currentSelectedCanvasImageRef.current.getSelectorActionAt(
                cords.x,
                cords.y
            );
            if (action) {
                selectedImageAction = true;
                selectedImageActionRef.current = action;

                if (action === "rotate") {
                    const image = currentSelectedCanvasImageRef.current;
                    const TL = image.TL_imageBounds;
                    const BR = image.BR_imageBounds;
                    const centerX = TL.x + (BR.x - TL.x) / 2;
                    const centerY = TL.y + (BR.y - TL.y) / 2;

                    const dx = cords.x - centerX;
                    const dy = cords.y - centerY;
                    const initialAngle = (Math.atan2(dy, dx) * 180) / Math.PI;

                    initialRotationRef.current = image.rotation;
                    initialAngleRef.current = initialAngle;
                } else if (action.startsWith("resize-")) {
                    const image = currentSelectedCanvasImageRef.current;
                    initialBoundsRef.current = {
                        TL: { ...image.TL_imageBounds },
                        BR: { ...image.BR_imageBounds },
                    };
                    const width =
                        initialBoundsRef.current.BR.x - initialBoundsRef.current.TL.x;
                    const height =
                        initialBoundsRef.current.BR.y - initialBoundsRef.current.TL.y;
                    aspectRatioRef.current = width / height;

                    const centerX =
                        (initialBoundsRef.current.TL.x + initialBoundsRef.current.BR.x) / 2;
                    const centerY =
                        (initialBoundsRef.current.TL.y + initialBoundsRef.current.BR.y) / 2;

                    const angleRad = (-image.rotation * Math.PI) / 180;
                    const dx = cords.x - centerX;
                    const dy = cords.y - centerY;

                    const localX = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
                    const localY = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);

                    initialPointerLocalPosRef.current = { x: localX, y: localY };
                }
            } else {
                selectedImageActionRef.current = null;
            }
        }

        if (!selectedImageAction) {
            for (let i = CanvasDrawer.canvasImages.length - 1; i >= 0; i--) {
                const image = CanvasDrawer.canvasImages[i];
                const TL = image.TL_imageBounds;
                const BR = image.BR_imageBounds;

                const width = BR.x - TL.x;
                const height = BR.y - TL.y;

                const centerX = TL.x + width / 2;
                const centerY = TL.y + height / 2;

                const dx = cords.x - centerX;
                const dy = cords.y - centerY;

                const angleRad = (-image.rotation * Math.PI) / 180;
                const localX = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
                const localY = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);

                const withinX = localX >= -width / 2 && localX <= width / 2;
                const withinY = localY >= -height / 2 && localY <= height / 2;

                if (withinX && withinY) {
                    const namedImage = ImageStore.namedImages.find(ni => ni.imgID === image.imageID);
                    if (namedImage) {
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = namedImage.img.width;
                        tempCanvas.height = namedImage.img.height;
                        const tempCtx = tempCanvas.getContext('2d');

                        if (tempCtx) {
                            tempCtx.drawImage(namedImage.img, 0, 0);

                            const imgX = (localX + width / 2) * (namedImage.img.width / width);
                            const imgY = (localY + height / 2) * (namedImage.img.height / height);

                            const pixelData = tempCtx.getImageData(Math.floor(imgX), Math.floor(imgY), 1, 1).data;
                            const alpha = pixelData[3];

                            if (alpha < 10) {
                                continue;
                            }
                        }
                    }

                    if (currentSelectedCanvasImageRef.current)
                        currentSelectedCanvasImageRef.current.selected = false;

                    currentSelectedCanvasImageRef.current = image;

                    updateLayerStatus(image)

                    updateUndoRedo()

                    setCanvasImageSelected(true)
                    image.selected = true;
                    foundCanvasImage = true;
                    CanvasDrawer.rerenderCanvas();
                    lastTouchPosition.current = { x: cords.x, y: cords.y };
                    break;
                }
            }
        }

        if (!foundCanvasImage && currentSelectedCanvasImageRef.current && !selectedImageAction) {
            currentSelectedCanvasImageRef.current.selected = false;
            currentSelectedCanvasImageRef.current = null;
            currentSelectedCanvasImageRef.current = null
            setCanvasImageSelected(false)
            CanvasDrawer.rerenderCanvas();
        }

        lastTouchPosition.current = { x: cords.x, y: cords.y };
    };

    const selectMoveMouse = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const MIN_STICKER_AREA = 75000

        if (!CanvasDrawer.canvas) return;

        const rect = CanvasDrawer.canvas.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const x = (e.clientX - rect.left - centerX - CanvasDrawer.offset.x) / CanvasDrawer.scale;
        const y = (e.clientY - rect.top - centerY - CanvasDrawer.offset.y) / CanvasDrawer.scale;
        const cords = { x, y };

        const image = currentSelectedCanvasImageRef.current;
        const action = selectedImageActionRef.current;
        if (!image || !lastTouchPosition.current) return;
        console.log(action)
        if (!action) {
            const diffX = cords.x - lastTouchPosition.current.x;
            const diffY = cords.y - lastTouchPosition.current.y;

            image.BR_imageBounds.x += diffX;
            image.TL_imageBounds.x += diffX;
            image.BR_imageBounds.y += diffY;
            image.TL_imageBounds.y += diffY;

            const now = performance.now();
            if (now - lastRender > 10) {
                CanvasDrawer.rerenderCanvas();
                lastRender = now;
            }

            actionRef.current = true;
        } else if (action === "rotate") {
            if (initialRotationRef.current === null || initialAngleRef.current === null) return;

            const TL = image.TL_imageBounds;
            const BR = image.BR_imageBounds;
            const centerX = TL.x + (BR.x - TL.x) / 2;
            const centerY = TL.y + (BR.y - TL.y) / 2;

            const dx = cords.x - centerX;
            const dy = cords.y - centerY;
            const currentAngle = (Math.atan2(dy, dx) * 180) / Math.PI;

            const angleDiff = currentAngle - initialAngleRef.current;
            image.rotation = initialRotationRef.current + angleDiff;

            CanvasDrawer.rerenderCanvas();

            actionRef.current = true;
        } else if (action.startsWith("resize-")) {

            if (!initialBoundsRef.current || aspectRatioRef.current === null || !initialPointerLocalPosRef.current) {
                console.log("1" + initialBoundsRef.current)
                console.log("2" + aspectRatioRef.current)
                console.log("3" + initialPointerLocalPosRef.current)
                return;
            }

            const initialTL = initialBoundsRef.current.TL;
            const initialBR = initialBoundsRef.current.BR;
            const aspectRatio = aspectRatioRef.current;

            const centerX = (initialTL.x + initialBR.x) / 2;
            const centerY = (initialTL.y + initialBR.y) / 2;

            const angleRad = (-image.rotation * Math.PI) / 180;
            const dx = cords.x - centerX;
            const dy = cords.y - centerY;

            const localX = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
            const localY = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);

            const initialLocalX = initialPointerLocalPosRef.current.x;
            const initialLocalY = initialPointerLocalPosRef.current.y;

            let newWidth = 0;
            let newHeight = 0;

            actionRef.current = true;

            switch (action) {
                case "resize-tl": {
                    let widthChange = initialLocalX - localX;
                    let heightChange = initialLocalY - localY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newTLx = centerX - newWidth / 2;
                    const newTLy = centerY - newHeight / 2;

                    image.TL_imageBounds.x = newTLx;
                    image.TL_imageBounds.y = newTLy;
                    image.BR_imageBounds.x = centerX + newWidth / 2;
                    image.BR_imageBounds.y = centerY + newHeight / 2;
                    break;
                }
                case "resize-tr": {
                    let widthChange = localX - initialLocalX;
                    let heightChange = initialLocalY - localY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newBRx = centerX + newWidth / 2;
                    const newTLy = centerY - newHeight / 2;

                    image.BR_imageBounds.x = newBRx;
                    image.TL_imageBounds.y = newTLy;
                    image.TL_imageBounds.x = newBRx - newWidth;
                    image.BR_imageBounds.y = newTLy + newHeight;
                    break;
                }
                case "resize-bl": {
                    let widthChange = initialLocalX - localX;
                    let heightChange = localY - initialLocalY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newTLx = centerX - newWidth / 2;
                    const newBRy = centerY + newHeight / 2;

                    image.TL_imageBounds.x = newTLx;
                    image.BR_imageBounds.y = newBRy;
                    image.BR_imageBounds.x = newTLx + newWidth;
                    image.TL_imageBounds.y = newBRy - newHeight;
                    break;
                }
                case "resize-br": {
                    let widthChange = localX - initialLocalX;
                    let heightChange = localY - initialLocalY;

                    if (widthChange / aspectRatio > heightChange) {
                        heightChange = widthChange / aspectRatio;
                    } else {
                        widthChange = heightChange * aspectRatio;
                    }

                    newWidth = (initialBR.x - initialTL.x) + widthChange * 2;
                    newHeight = newWidth / aspectRatio;

                    if (newWidth * newHeight < MIN_STICKER_AREA) return;

                    const newBRx = centerX + newWidth / 2;
                    const newBRy = centerY + newHeight / 2;

                    image.BR_imageBounds.x = newBRx;
                    image.BR_imageBounds.y = newBRy;
                    image.TL_imageBounds.x = newBRx - newWidth;
                    image.TL_imageBounds.y = newBRy - newHeight;
                    break;
                }
            }

            if (image.BR_imageBounds.x < image.TL_imageBounds.x + 10) {
                image.BR_imageBounds.x = image.TL_imageBounds.x + 10;
            }
            if (image.BR_imageBounds.y < image.TL_imageBounds.y + 10) {
                image.BR_imageBounds.y = image.TL_imageBounds.y + 10;
            }

            CanvasDrawer.rerenderCanvas();
        }

        lastTouchPosition.current = { x: cords.x, y: cords.y };
    };

    const sendCanvasImageToBottom = (image: CanvasImage) => {
        const array = CanvasDrawer.canvasImages
        const index = image.itemIndex

        array.splice(index, 1)

        array.unshift(image)

        array.forEach((img, idx) => {
            img.itemIndex = idx;
        });

        CanvasDrawer.canvasImages = array

        CanvasDrawer.rerenderCanvas()

        UndoRedoHelper.createSnapShot()

        updateLayerStatus(image)
        updateUndoRedo()
    }

    const sendCanvasImageToTop = (image: CanvasImage) => {
        const array = CanvasDrawer.canvasImages
        const index = image.itemIndex

        array.splice(index, 1)

        array.push(image)

        array.forEach((img, idx) => {
            img.itemIndex = idx;
        });

        CanvasDrawer.canvasImages = array

        CanvasDrawer.rerenderCanvas()

        UndoRedoHelper.createSnapShot()

        updateLayerStatus(image)
        updateUndoRedo()
    }

    const moveSelectedDownOneLayer = (image: CanvasImage) => {
        const startingImageData = CanvasDrawer.ctx!.getImageData(0, 0, CanvasDrawer.canvas!.width, CanvasDrawer.canvas!.height);
        const originalItemIndex = image.itemIndex;
        const startingList = CanvasDrawer.canvasImages;
        let foundItem = false;

        for (let i = 1; i <= originalItemIndex; i++) {
            const newList = [...startingList];
            const [removedItem] = newList.splice(originalItemIndex, 1);
            newList.splice(originalItemIndex - i, 0, removedItem);

            CanvasDrawer.canvasImages = newList;
            CanvasDrawer.rerenderCanvas();

            const newCanvasData = CanvasDrawer.ctx!.getImageData(0, 0, CanvasDrawer.canvas!.width, CanvasDrawer.canvas!.height);

            if (!startingImageData.data.every((value, index) => value === newCanvasData.data[index])) {
                for (let j = 0; j < newList.length; j++) {
                    newList[j].itemIndex = j;
                }
                image.itemIndex = originalItemIndex - i;
                CanvasDrawer.canvasImages = newList;
                foundItem = true;
                break;
            }
        }

        if (!foundItem) {
            const fallbackList = [...startingList];
            const [removedItem] = fallbackList.splice(originalItemIndex, 1);
            fallbackList.unshift(removedItem);

            for (let j = 0; j < fallbackList.length; j++) {
                fallbackList[j].itemIndex = j;
            }
            image.itemIndex = 0;
            CanvasDrawer.canvasImages = fallbackList;
        }

        CanvasDrawer.rerenderCanvas();

        UndoRedoHelper.createSnapShot()

        updateLayerStatus(image)
        updateUndoRedo()
    };

    const moveSelectedUpOneLayer = (image: CanvasImage) => {
        const startingImageData = CanvasDrawer.ctx!.getImageData(
            0,
            0,
            CanvasDrawer.canvas!.width,
            CanvasDrawer.canvas!.height
        );

        const itemIndex = image.itemIndex;
        const iterations = CanvasDrawer.canvasImages.length - 1 - itemIndex;
        let foundItem = false;

        const startingList = CanvasDrawer.canvasImages;

        for (let i = 1; i <= iterations; i++) {
            const newList = [...startingList];
            const [removedItem] = newList.splice(itemIndex, 1);
            newList.splice(itemIndex + i, 0, removedItem);

            CanvasDrawer.canvasImages = newList;

            CanvasDrawer.rerenderCanvas();

            const newCanvasData = CanvasDrawer.ctx!.getImageData(
                0,
                0,
                CanvasDrawer.canvas!.width,
                CanvasDrawer.canvas!.height
            );

            if (!startingImageData.data.every((value, index) => value === newCanvasData.data[index])) {
                for (let j = 0; j < newList.length; j++) {
                    newList[j].itemIndex = j;
                }

                image.itemIndex = itemIndex + i;
                foundItem = true;
                break;
            }
        }

        if (!foundItem) {
            const fallbackList = [...CanvasDrawer.canvasImages];
            const [removedItem] = fallbackList.splice(itemIndex, 1);
            fallbackList.push(removedItem);

            for (let j = 0; j < fallbackList.length; j++) {
                fallbackList[j].itemIndex = j;
            }

            image.itemIndex = fallbackList.length - 1;
            CanvasDrawer.canvasImages = fallbackList;
        }

        CanvasDrawer.rerenderCanvas();

        UndoRedoHelper.createSnapShot()

        updateLayerStatus(image)
        updateUndoRedo()
    };


    const deleteSelectedItem = (item: CanvasImage) => {
        const arr = CanvasDrawer.canvasImages
        arr.splice(item.itemIndex, 1)

        arr.forEach((img, idx) => {
            img.itemIndex = idx;
        });

        CanvasDrawer.canvasImages = arr
        setCanvasImageSelected(false)
        currentSelectedCanvasImageRef.current = null
        CanvasDrawer.allocateGhostArrayImages()
        CanvasDrawer.rerenderCanvas()
        UndoRedoHelper.createSnapShot()
        updateUndoRedo()
    }

    const flipSelectedImage = (image: CanvasImage) => {
        image.flipped = !image.flipped
        CanvasDrawer.rerenderCanvas()
        UndoRedoHelper.createSnapShot()
        updateUndoRedo()
    }



    return {
        startSelect,
        selectMove,
        startSelectMouse,
        selectMoveMouse,
        sendCanvasImageToBottom,
        sendCanvasImageToTop,
        moveSelectedDownOneLayer,
        moveSelectedUpOneLayer,
        deleteSelectedItem,
        flipSelectedImage,
        endSelect
    };


}
